import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Link, Paragraph, Tag } from '@digdir/designsystemet-react';
import type { WorkEnvironmentRisk } from '../../../api/workEnvironmentTypes';
import { useRemindSafetyRepsMutation } from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { mailtoLink, reminderText, reviewerMissing } from '../assessmentView';
import { reminderStatus } from '../reminderView';
import classes from '../WorkEnvironmentSurveyRisk.module.css';
import { TakenOverNotice } from './TakenOverNotice';

type Assessment = NonNullable<Extract<WorkEnvironmentRisk, { calculatedAt: string }>['assessment']>;

type Props = { surveyId: number; calculatedAt: string; assessment: Assessment };

/** Who the team leader goes through the risk with, and how to reach them */
export const ReviewerNotice = ({ surveyId, calculatedAt, assessment }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const remind = useRemindSafetyRepsMutation(surveyId);
  const { sent, failed } = reminderStatus(remind, t, i18n.language);
  const { safetyReps, hrEmails } = assessment;
  const missing = `workEnvironmentRisk.assessment.${reviewerMissing(assessment)}`;

  if (safetyReps.length === 0)
    return (
      <Alert data-color="warning">
        <Heading level={3} data-size="2xs">
          {t(`${missing}.title`)}
        </Heading>
        <Paragraph data-size="sm">{t(`${missing}.text`)}</Paragraph>
        <Paragraph data-size="sm">
          {t(`${missing}.hr`)}{' '}
          {hrEmails.length > 0 && (
            <Link href={mailtoLink(hrEmails)}>{t('workEnvironmentRisk.assessment.noRep.contactHr')}</Link>
          )}
        </Paragraph>
      </Alert>
    );

  return (
    <div className={classes.reviewer}>
      <div className={classes.areaSummary}>
        <span className="ds-label" data-size="sm">
          {t('workEnvironmentRisk.assessment.rep.title', { count: safetyReps.length })}
        </span>
        <Tag data-size="sm" data-color="info">
          {reminderText(assessment.remindedAt, calculatedAt, t, i18n.language)}
        </Tag>
      </div>
      <Paragraph data-size="sm">{t('workEnvironmentRisk.assessment.rep.text')}</Paragraph>
      <TakenOverNotice emails={assessment.takenOverBy} />
      {sent && (
        <Paragraph data-size="sm" role="status">
          {sent}
        </Paragraph>
      )}
      {failed && (
        <Alert data-color="danger">
          <Paragraph>{failed}</Paragraph>
        </Alert>
      )}
      <div className={classes.reviewerContact}>
        {safetyReps.map((email) => (
          <Link key={email} href={mailtoLink([email])} data-size="sm">
            {email}
          </Link>
        ))}
        {assessment.canRemind && (
          <Button
            variant="tertiary"
            data-size="sm"
            loading={remind.isPending}
            onClick={() => remind.mutate(undefined)}
          >
            {t('workEnvironmentRisk.assessment.rep.remind')}
          </Button>
        )}
      </div>
    </div>
  );
};
